import { trainingZoneColor, defaultPowerZoneForPercent } from '../utils/trainingZones';
import '../styles/workout-editor-system.css';
import { apiStructureToBuilderSteps, type ApiWorkoutStructure } from '../utils/workoutStructure';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  builderStepsToApiStructure,
  FTP_PERCENT_MAX,
  type ApiStructBlock,
  type BuilderWorkoutStep,
} from '../utils/workoutStructure';
import { calculatePlannedMetricsFromStructure } from '../utils/plannedWorkoutMetrics';
import { forms, library, workouts } from '../services/api';
import { useAthleteMetrics } from '../hooks/useAthleteMetrics';
import { WorkoutBuilderSetupModal } from '../components/WorkoutBuilderSetupModal';
import { MobileWorkoutBuilderShell } from '../features/workout/builder/MobileWorkoutBuilderShell';
import {
  chartTitleForMetric,
  formatStepIntensityLine,
  intensityMetricShortLabel,
  intensityPercentMax,
  isRunningBuilderSettings,
  parseWorkoutBuilderSettings,
  referenceThresholdLabel,
  settingsSummaryLabel,
  type IntensityThresholds,
  type WorkoutBuilderSettings,
} from '../utils/workoutBuilderSettings';

// ─── Zone constants ────────────────────────────────────────────────────────────
const ZONES: Record<number, { label: string; color: string; name: string }> = {
  1: { label: 'Z1', color: trainingZoneColor(1), name: 'Recovery' },
  2: { label: 'Z2', color: trainingZoneColor(2), name: 'Aerobic' },
  3: { label: 'Z3', color: trainingZoneColor(3), name: 'Tempo' },
  4: { label: 'Z4', color: trainingZoneColor(4), name: 'Threshold' },
  5: { label: 'Z5', color: trainingZoneColor(5), name: 'VO2 Max' },
  6: { label: 'Z6', color: trainingZoneColor(6), name: 'Anaerobic' },
  7: { label: 'Z7', color: trainingZoneColor(7), name: 'Neuromuscular' },
};

const ZONE_RANGES: Record<number, [number, number]> = {
  1: [0, 55],
  2: [56, 75],
  3: [76, 90],
  4: [91, 105],
  5: [106, 400],
};

// ─── Types ─────────────────────────────────────────────────────────────────────
interface SimpleStep {
  id: number;
  type: 'simple';
  name: string;
  duration: number;
  min: number;
  max: number;
  zone: number;
  isRamp: boolean;
}

interface RepeatBlock {
  id: number;
  type: 'repeat';
  /** Group label shown in the step list (e.g. Ramp Up / Ramp Down). */
  name?: string;
  count: number;
  steps: SimpleStep[];
}

type WorkoutStep = SimpleStep | RepeatBlock;

interface BlockTemplateStep {
  name: string;
  duration: number;
  min: number;
  max: number;
  zone: number;
  isRamp?: boolean;
}

interface BlockTemplate {
  key: string;
  name: string;
  zone: number;
  duration: number;
  min: number;
  max: number;
  isRamp: boolean;
  /** Multi-step preset — adds several steady stages when dropped onto the timeline. */
  steps?: BlockTemplateStep[];
  previewType?: 'ramp' | 'stairs-up' | 'stairs-down' | 'interval-pair' | 'steady';
}

/** 3-stage ascending preset (Ramp Up). */
const STAIRS_UP_STEPS: BlockTemplateStep[] = [
  { name: 'Step 1', duration: 100, min: 60, max: 65, zone: 2 },
  { name: 'Step 2', duration: 100, min: 70, max: 75, zone: 2 },
  { name: 'Step 3', duration: 100, min: 85, max: 90, zone: 3 },
];

/** 3-stage descending preset (Ramp Down). */
const STAIRS_DOWN_STEPS: BlockTemplateStep[] = [
  { name: 'Step 1', duration: 100, min: 85, max: 90, zone: 3 },
  { name: 'Step 2', duration: 100, min: 70, max: 75, zone: 2 },
  { name: 'Step 3', duration: 100, min: 55, max: 60, zone: 2 },
];

/** 2-step interval preset — Hard + Recovery (repeatable). */
const INTERVAL_PAIR_STEPS: BlockTemplateStep[] = [
  { name: 'Hard', duration: 480, min: 97, max: 107, zone: 4 },
  { name: 'Recovery', duration: 300, min: 35, max: 45, zone: 1 },
];

// ─── Block templates ───────────────────────────────────────────────────────────
/** Palette order on mobile — mirrors Ai-Tempo `PALETTE_BLOCKS`. */
const MOBILE_PALETTE_KEYS = [
  'warmup',
  'active',
  'interval-pair',
  'recovery',
  'cooldown',
  'stairs-up',
  'stairs-down',
] as const;

const BLOCK_TEMPLATES: BlockTemplate[] = [
  { key: 'warmup',      name: 'Warm Up',         zone: 2, duration: 600, min: 45, max: 60,  isRamp: true,  previewType: 'ramp' },
  { key: 'active',      name: 'Active',          zone: 2, duration: 900, min: 70, max: 80,  isRamp: false, previewType: 'steady' },
  { key: 'stairs-up',      name: 'Ramp Up',   zone: 3, duration: 300, min: 60, max: 90,  isRamp: false, previewType: 'stairs-up', steps: STAIRS_UP_STEPS },
  { key: 'stairs-down',    name: 'Ramp Down', zone: 2, duration: 300, min: 55, max: 90,  isRamp: false, previewType: 'stairs-down', steps: STAIRS_DOWN_STEPS },
  { key: 'interval-pair',  name: 'Interval',  zone: 4, duration: 780, min: 35, max: 107, isRamp: false, previewType: 'interval-pair', steps: INTERVAL_PAIR_STEPS },
  { key: 'steady',         name: 'Steady',    zone: 3, duration: 600, min: 76, max: 85,  isRamp: false, previewType: 'steady' },
  { key: 'interval',       name: 'Hard',      zone: 4, duration: 480, min: 97, max: 107, isRamp: false, previewType: 'steady' },
  { key: 'recovery',    name: 'Recovery',        zone: 1, duration: 300, min: 35, max: 45,  isRamp: false, previewType: 'steady' },
  { key: 'cooldown',    name: 'Cool Down',       zone: 1, duration: 600, min: 35, max: 50,  isRamp: true,  previewType: 'ramp' },
];

/** Deep-clone a simple step with a fresh id. */
function cloneSimpleStep(step: SimpleStep, newId: number): SimpleStep {
  return { ...step, id: newId };
}

/** Deep-clone a repeat block and assign new ids to the block and its substeps. */
function cloneRepeatBlock(block: RepeatBlock, newBlockId: number): RepeatBlock {
  return {
    ...block,
    id: newBlockId,
    steps: block.steps.map((st, i) => cloneSimpleStep(st, newBlockId + i + 1)),
  };
}

/** Build a named repeat block from a multi-stage template (Ramp Up / Ramp Down). */
function buildNamedRepeatBlock(blockId: number, blockName: string, templateSteps: BlockTemplateStep[]): RepeatBlock {
  return {
    id: blockId,
    type: 'repeat',
    name: blockName,
    count: 1,
    steps: templateSteps.map((st, i) => ({
      id: blockId + i + 1,
      type: 'simple',
      name: st.name,
      duration: st.duration,
      min: st.min,
      max: st.max,
      zone: st.zone,
      isRamp: st.isRamp ?? false,
    })),
  };
}

const INIT_STEPS: WorkoutStep[] = [
  { id: 1, type: 'simple', name: 'Warm Up', duration: 600, min: 45, max: 60, zone: 2, isRamp: false },
  { id: 2, type: 'simple', name: 'Active', duration: 900, min: 65, max: 75, zone: 2, isRamp: false },
  { id: 3, type: 'simple', name: 'Cool Down', duration: 600, min: 35, max: 50, zone: 1, isRamp: false },
];

// ─── Utility functions ─────────────────────────────────────────────────────────
function fmtDur(secs: number): string {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function parseDur(str: string): number {
  const p = str.split(':').map(n => parseInt(n) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return (p[0] || 5) * 60;
}

function flatSteps(steps: WorkoutStep[]): SimpleStep[] {
  const out: SimpleStep[] = [];
  steps.forEach(s => {
    if (s.type === 'repeat') {
      for (let i = 0; i < s.count; i++) s.steps.forEach(x => out.push(x));
    } else {
      out.push(s);
    }
  });
  return out;
}

function calcTotals(
  steps: WorkoutStep[],
  workoutSettings: WorkoutBuilderSettings,
  thresholds: IntensityThresholds,
) {
  const structure = builderStepsToApiStructure(steps as BuilderWorkoutStep[], {
    ...workoutSettings,
    thresholdSpeedMps: thresholds.thresholdSpeedMps,
    ftp: thresholds.ftp,
    ltHr: thresholds.ltHr,
    maxHr: thresholds.maxHr,
  });
  const m = calculatePlannedMetricsFromStructure(structure, {
    ftp: thresholds.ftp,
    ltHr: thresholds.ltHr,
    maxHr: thresholds.maxHr,
    thresholdSpeedMps: thresholds.thresholdSpeedMps,
  });
  const ifVal = m.if_planned ?? 0;
  const kj =
    workoutSettings.primaryIntensityMetric === 'percentOfFtp' && thresholds.ftp > 0
      ? Math.round((m.total_time_planned * ifVal * thresholds.ftp) / 1000)
      : 0;

  return {
    secs: m.total_time_planned,
    meters: m.distance_planned ?? 0,
    kj,
    tss: m.tss_planned ?? 0,
    ifVal: ifVal.toFixed(2),
    count: flatSteps(steps).length,
  };
}

function chartIntensityScale(
  workoutSettings: WorkoutBuilderSettings,
  segs: SimpleStep[],
): number {
  const metricMax = intensityPercentMax(workoutSettings.primaryIntensityMetric);
  const peak = segs.reduce((m, s) => Math.max(m, s.min, s.max), 0);
  return Math.max(metricMax, peak, 150);
}

function zoneFor(pct: number): number {
  return defaultPowerZoneForPercent(pct);
}

/** Mini chart thumbnail for block library pills. */
function renderBlockPreview(t: BlockTemplate, color: string): React.ReactNode {
  const H = 24;
  const stairs = (heights: number[]) =>
    heights.map((h, i) => {
      const w = 100 / heights.length;
      const x = i * w;
      return <rect key={i} x={x} y={H - h} width={w - 0.5} height={h} fill={color} opacity={0.85} />;
    });

  if (t.previewType === 'stairs-up') return stairs([8, 14, 20]);
  if (t.previewType === 'stairs-down') return stairs([20, 14, 8]);
  if (t.previewType === 'interval-pair') {
    const z4 = ZONES[4].color;
    const z1 = ZONES[1].color;
    return (
      <>
        <rect x={0} y={H - 20} width={62} height={20} fill={z4} opacity={0.85} />
        <rect x={64} y={H - 8} width={36} height={8} fill={z1} opacity={0.85} />
      </>
    );
  }
  if (t.isRamp) {
    return (
      <polygon
        points={`0,${H} 0,${H - t.min / FTP_PERCENT_MAX * H} 100,${H - t.max / FTP_PERCENT_MAX * H} 100,${H}`}
        fill={color}
        opacity={0.85}
      />
    );
  }
  const mid = (t.min + t.max) / 2;
  const bh = mid / FTP_PERCENT_MAX * H;
  return <rect x={0} y={H - bh} width={100} height={bh} fill={color} opacity={0.85} />;
}

function findStep(steps: WorkoutStep[], id: number | null): SimpleStep | null {
  if (id === null) return null;
  for (const s of steps) {
    if (s.id === id && s.type === 'simple') return s;
    if (s.type === 'repeat') {
      for (const x of s.steps) if (x.id === id) return x;
    }
  }
  return null;
}

// ─── Power Chart ───────────────────────────────────────────────────────────────
interface PowerChartProps {
  steps: WorkoutStep[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
}

function PowerChart({ steps, selectedId, onSelect, workoutSettings, thresholds }: PowerChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(800);
  const [tip, setTip] = useState<SimpleStep | null>(null);
  const [mxy, setMxy] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(e => setCw(Math.floor(e[0].contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const H = 118, PL = 28, PR = 8, VH = H + 24;
  const W = Math.max(cw - PL - PR, 100);

  const segs: (SimpleStep & { _r?: boolean })[] = [];
  steps.forEach(s => {
    if (s.type === 'repeat') {
      for (let i = 0; i < s.count; i++) s.steps.forEach(x => segs.push({ ...x, _r: true }));
    } else {
      segs.push(s);
    }
  });
  const MAX = chartIntensityScale(workoutSettings, segs);
  const totalDur = segs.reduce((a, s) => a + s.duration, 0) || 1;

  let cx = PL;
  const bars = segs.map((seg, i) => {
    const w = Math.max((seg.duration / totalDur) * W, 3);
    const displayZone = zoneFor((seg.min + seg.max) / 2);
    const z = ZONES[displayZone] || ZONES[1];
    const isSel = seg.id === selectedId;
    const alpha = isSel ? 1 : 0.72;

    let shape: React.ReactNode;
    if (seg.isRamp) {
      const high = Math.max(seg.min, seg.max);
      const bh = high / MAX * H;
      shape = (
        <rect
          x={cx + 0.5}
          y={H - bh}
          width={w - 1}
          height={bh}
          rx={Math.min(3, (w - 1) / 2)}
          fill={z.color}
          opacity={alpha}
        />
      );
    } else {
      const mid = (seg.min + seg.max) / 2, bh = mid / MAX * H;
      shape = <rect x={cx + 0.5} y={H - bh} width={w - 1} height={bh}
        rx={Math.min(3, (w - 1) / 2)} fill={z.color} opacity={alpha} />;
    }

    const ox = cx; cx += w;
    return (
      <g key={i} style={{ cursor: 'pointer' }}
        onClick={() => onSelect(seg.id === selectedId ? null : seg.id)}
        onMouseEnter={() => setTip(seg)}
        onMouseLeave={() => setTip(null)}>
        {shape}
        {isSel && (
          <>
            <rect x={ox} y={0} width={w} height={H} fill={z.color} fillOpacity={0.1} />
            <rect x={ox} y={H - 3} width={w} height={3} fill={z.color} />
          </>
        )}
      </g>
    );
  });

  const gridStep = MAX <= 150 ? 50 : 100;
  const gridPctMarks: number[] = [];
  for (let p = gridStep; p < MAX; p += gridStep) gridPctMarks.push(p);
  if (!gridPctMarks.length || gridPctMarks[gridPctMarks.length - 1] !== MAX) {
    gridPctMarks.push(MAX);
  }

  const grid = gridPctMarks.map(p => {
    const y = H - (p / MAX) * H;
    return (
      <g key={p}>
        <line x1={PL} y1={y} x2={cw - PR} y2={y}
          stroke="rgba(26,31,43,0.07)"
          strokeWidth={p === 100 ? 1.5 : 0.7}
          strokeDasharray={p === 100 ? undefined : '4,5'} />
        <text x={PL - 5} y={y + 3.5} fontSize={8} textAnchor="end"
          fontFamily="Inter,sans-serif"
          fill="rgba(107,114,130,0.55)">{p}%</text>
      </g>
    );
  });

  const tm = Math.round(totalDur / 60), intv = tm <= 30 ? 5 : tm <= 60 ? 10 : 15;
  const tl: React.ReactNode[] = [];
  for (let m = 0; m <= tm; m += intv) {
    const xp = PL + (m * 60 / totalDur) * W;
    const h = Math.floor(m / 60), mn = m % 60;
    const lbl = h > 0 ? `${h}:${String(mn).padStart(2, '0')}` : `${String(m).padStart(2, '0')}:00`;
    tl.push(
      <text key={m} x={xp} y={H + 17} fontSize={8.5} textAnchor="middle"
        fontFamily="Inter,sans-serif"
        fill="rgba(107,114,130,0.55)">{lbl}</text>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}
      onMouseMove={e => {
        const r = e.currentTarget.getBoundingClientRect();
        setMxy({ x: e.clientX - r.left, y: e.clientY - r.top });
      }}>
      {cw > 50 && (
        <svg viewBox={`0 0 ${cw} ${VH}`} width={cw} height={VH} style={{ display: 'block' }}>
          {grid}{bars}{tl}
        </svg>
      )}
      {tip && (
        <div style={{
          position: 'absolute', pointerEvents: 'none', zIndex: 30,
          left: mxy.x + 14, top: Math.max(4, mxy.y - 54),
          background: 'rgba(18,22,32,0.9)', backdropFilter: 'blur(8px)',
          borderRadius: 9, padding: '8px 12px',
          boxShadow: '0 4px 18px rgba(0,0,0,0.28)',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 2 }}>{tip.name}</div>
          <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.52)', fontVariantNumeric: 'tabular-nums' }}>
            {fmtDur(tip.duration)} · {formatStepIntensityLine(tip.min, tip.max, workoutSettings.primaryIntensityMetric, thresholds)}{(tip as SimpleStep & { _r?: boolean })._r ? ' · repeat' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Block Palette (left panel) ────────────────────────────────────────────────
interface BlockPaletteProps {
  onAddBlock: (t: BlockTemplate) => void;
  onAddRepeat: () => void;
  accent: string;
}

function BlockPalette({ onAddBlock, onAddRepeat, accent }: BlockPaletteProps) {
  return (
    <div style={s.leftPanel}>
      <div style={s.sectLbl}>Block Library</div>
      <div style={s.blockGrid}>
        {BLOCK_TEMPLATES.map(t => {
          const z = ZONES[t.zone];
          return (
            <button key={t.key} style={s.blockPill} onClick={() => onAddBlock(t)}
              onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--nm-raised)')}
              onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)')}
              onMouseDown={e => (e.currentTarget.style.boxShadow = 'var(--nm-inset-sm)')}
              onMouseUp={e => (e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)')}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--fg-2)', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', padding: '2px 5px', borderRadius: 4, background: z.color, flexShrink: 0 }}>{z.label}</span>
              </div>
              <div style={{ borderRadius: 6, overflow: 'hidden', background: 'var(--surface-sunken)', boxShadow: 'var(--nm-inset-sm)' }}>
                <svg width="100%" height="24" viewBox="0 0 100 24" preserveAspectRatio="none">
                  {renderBlockPreview(t, z.color)}
                </svg>
              </div>
              <span style={{ fontSize: 9, fontWeight: 600, color: 'var(--fg-4)', fontVariantNumeric: 'tabular-nums' }}>{fmtDur(t.duration)}</span>
            </button>
          );
        })}
      </div>

      <button style={s.repeatAddBtn} onClick={onAddRepeat}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)'; e.currentTarget.style.color = 'var(--fg-1)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--nm-flat)'; e.currentTarget.style.color = 'var(--fg-3)'; }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round">
          <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
        Add Repeat Block
      </button>

    </div>
  );
}

// ─── Mobile palette (horizontal scroll — Ai-Tempo) ───────────────────────────

function buildMobilePillBars(def: BlockTemplate): { bars: { h: number; color: string }[]; repeatCount?: number } {
  if (def.steps?.length) {
    const unit = def.steps.map(st => ({
      h: Math.max(3, ((st.min + st.max) / 2 / 100) * 28),
      color: ZONES[st.zone]?.color ?? ZONES[2].color,
    }));
    const filled: { h: number; color: string }[] = [];
    while (filled.length < 6) filled.push(...unit);
    const repeatCount = def.key === 'interval-pair' ? 2 : undefined;
    return { bars: filled.slice(0, 6), repeatCount };
  }
  const z = ZONES[def.zone] ?? ZONES[2];
  if (def.isRamp) {
    const lo = Math.max(3, (def.min / 100) * 28);
    const hi = Math.max(3, (def.max / 100) * 28);
    return { bars: [{ h: lo, color: z.color }, { h: hi, color: z.color }] };
  }
  const mid = Math.max(3, ((def.min + def.max) / 2 / 100) * 28);
  return { bars: [{ h: mid, color: z.color }] };
}

interface MobilePaletteRowProps {
  templates: BlockTemplate[];
  onAddBlock: (t: BlockTemplate) => void;
  labelFor: (t: BlockTemplate) => string;
}

function MobilePaletteRow({ templates, onAddBlock, labelFor }: MobilePaletteRowProps) {
  return (
    <div className="wb-mobile-palette-wrap">
      <div className="wb-mobile-palette-scroll">
        {templates.map(tpl => {
          const { bars, repeatCount } = buildMobilePillBars(tpl);
          return (
            <button
              key={tpl.key}
              type="button"
              className="wb-mobile-palette-pill"
              onClick={() => onAddBlock(tpl)}>
              <span className="wb-mobile-palette-pill__label">{labelFor(tpl)}</span>
              <div className="wb-mobile-palette-pill__bars ltr-island">
                {bars.map((bar, i) => (
                  <div
                    key={i}
                    className="wb-mobile-palette-pill__bar"
                    style={{ height: bar.h, backgroundColor: bar.color }}
                  />
                ))}
                {repeatCount != null && (
                  <span className="wb-mobile-palette-pill__repeat">{repeatCount}×</span>
                )}
              </div>
            </button>
          );
        })}
        <div style={{ flex: '0 0 8px' }} aria-hidden />
      </div>
      <div className="wb-mobile-palette-fade" aria-hidden />
    </div>
  );
}

// ─── Mobile totals strip ─────────────────────────────────────────────────────

function MobileTotalsStrip({
  totals,
  accent,
  durationLabel,
  stepsLabel,
  kjLabel,
}: {
  totals: ReturnType<typeof calcTotals>;
  accent: string;
  durationLabel: string;
  stepsLabel: string;
  kjLabel: string;
}) {
  const h = Math.floor(totals.secs / 3600);
  const m = Math.floor((totals.secs % 3600) / 60);
  const durStr = h > 0
    ? `${h}:${String(m).padStart(2, '0')}h`
    : `${m}min`;

  return (
    <div className="wb-mobile-totals">
      <div className="wb-mobile-totals__stat">
        <span className="wb-mobile-totals__val" style={{ color: accent }}>{durStr}</span>
        <span className="wb-mobile-totals__lbl">{durationLabel}</span>
      </div>
      <div className="wb-mobile-totals__divider" />
      <div className="wb-mobile-totals__stat">
        <span className="wb-mobile-totals__val" style={{ color: '#1FAE63' }}>{totals.count}</span>
        <span className="wb-mobile-totals__lbl">{stepsLabel}</span>
      </div>
      <div className="wb-mobile-totals__divider" />
      <div className="wb-mobile-totals__stat">
        <span className="wb-mobile-totals__val" style={{ color: '#E0981A' }}>{totals.kj}</span>
        <span className="wb-mobile-totals__lbl">{kjLabel}</span>
      </div>
    </div>
  );
}

// ─── Mobile step cards ─────────────────────────────────────────────────────────

interface MobileStepListProps {
  steps: WorkoutStep[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
  repeatLabel: string;
}

function MobileStepCard({
  step,
  selected,
  onSelect,
  onDelete,
  workoutSettings,
  thresholds,
}: {
  step: SimpleStep;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
}) {
  const { t } = useTranslation();
  const z = ZONES[zoneFor((step.min + step.max) / 2)] || ZONES[1];
  const intensityLine = formatStepIntensityLine(
    step.min, step.max, workoutSettings.primaryIntensityMetric, thresholds,
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className={`wb-mobile-step-card${selected ? ' wb-mobile-step-card--selected' : ''}`}
      onClick={onSelect}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}>
      <div className="wb-mobile-step-card__accent" style={{ background: z.color }} />
      <div className="wb-mobile-step-card__body">
        <div className="wb-mobile-step-card__top">
          <span className="wb-mobile-step-card__name">{step.name === 'Active' ? t('workoutBuilder.palette.active') : step.name}</span>
          <button
            type="button"
            style={{ border: 'none', background: 'transparent', color: '#FF6B3D', padding: 4, flexShrink: 0 }}
            onClick={e => { e.stopPropagation(); onDelete(); }}
            aria-label="Delete">
            ✕
          </button>
        </div>
        <div className="wb-mobile-step-card__meta">
          <span className="wb-mobile-meta-chip ltr-island">⏱ {fmtDur(step.duration)}</span>
          <span className="wb-mobile-meta-chip ltr-island">⚡ {intensityLine}</span>
        </div>
      </div>
    </div>
  );
}

function MobileRepeatBlock({
  block,
  selectedId,
  onSelect,
  onDelete,
  onUpdate,
  workoutSettings,
  thresholds,
  repeatLabel,
}: {
  block: RepeatBlock;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onUpdate: (patch: Partial<RepeatBlock>) => void;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
  repeatLabel: string;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="wb-mobile-repeat">
      <button type="button" className="wb-mobile-repeat__header" onClick={() => setOpen(o => !o)}>
        <div className="wb-mobile-repeat__header-left">
          <span className="wb-mobile-repeat__count">{block.count}×</span>
          <span className="wb-mobile-repeat__label">{block.name ?? repeatLabel}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            type="button"
            style={{ border: 'none', background: 'var(--surface-sunken)', borderRadius: 8, width: 28, height: 28, fontWeight: 800 }}
            onClick={e => { e.stopPropagation(); onUpdate({ count: Math.max(1, block.count - 1) }); }}>−</button>
          <button
            type="button"
            style={{ border: 'none', background: 'var(--surface-sunken)', borderRadius: 8, width: 28, height: 28, fontWeight: 800 }}
            onClick={e => { e.stopPropagation(); onUpdate({ count: block.count + 1 }); }}>+</button>
          <button
            type="button"
            style={{ border: 'none', background: 'transparent', color: '#FF6B3D', padding: 4 }}
            onClick={e => { e.stopPropagation(); onDelete(block.id); }}>✕</button>
        </div>
      </button>
      {open && (
        <div className="wb-mobile-repeat__nested">
          {block.steps.map(sub => (
            <MobileStepCard
              key={sub.id}
              step={sub}
              selected={sub.id === selectedId}
              onSelect={() => onSelect(sub.id)}
              onDelete={() => onUpdate({ steps: block.steps.filter(x => x.id !== sub.id) })}
              workoutSettings={workoutSettings}
              thresholds={thresholds}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MobileStepList({
  steps, selectedId, onSelect, onDelete, onSetSteps,
  workoutSettings, thresholds, repeatLabel,
}: MobileStepListProps & { onSetSteps: React.Dispatch<React.SetStateAction<WorkoutStep[]>> }) {
  return (
    <div className="wb-mobile-steps">
      {steps.map(step => {
        if (step.type === 'repeat') {
          return (
            <MobileRepeatBlock
              key={step.id}
              block={step}
              selectedId={selectedId}
              onSelect={onSelect}
              onDelete={onDelete}
              onUpdate={patch => onSetSteps(prev => prev.map(x => x.id === step.id ? { ...x, ...patch } as RepeatBlock : x))}
              workoutSettings={workoutSettings}
              thresholds={thresholds}
              repeatLabel={repeatLabel}
            />
          );
        }
        return (
          <MobileStepCard
            key={step.id}
            step={step}
            selected={step.id === selectedId}
            onSelect={() => onSelect(step.id)}
            onDelete={() => onDelete(step.id)}
            workoutSettings={workoutSettings}
            thresholds={thresholds}
          />
        );
      })}
    </div>
  );
}

// ─── Totals Strip ──────────────────────────────────────────────────────────────
interface TotalsStripProps {
  totals: ReturnType<typeof calcTotals>;
  accent: string;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
}

function TotalsStrip({ totals, accent, workoutSettings, thresholds }: TotalsStripProps) {
  const ref = referenceThresholdLabel(workoutSettings.primaryIntensityMetric, thresholds);
  const powerMetrics = workoutSettings.primaryIntensityMetric === 'percentOfFtp';
  const showLoad = totals.tss > 0 || Number(totals.ifVal) > 0;
  const items = [
    { v: fmtDur(totals.secs), l: 'Duration', c: 'var(--fg-1)' },
    ...(totals.meters > 0
      ? [{ v: `${(totals.meters / 1000).toFixed(2)} km`, l: 'Distance', c: 'var(--fg-1)' }]
      : []),
    ...(showLoad
      ? [
          { v: String(totals.tss), l: 'TSS', c: accent },
          { v: totals.ifVal, l: 'IF', c: 'var(--fg-1)' },
          ...(powerMetrics ? [{ v: `${totals.kj}`, l: 'kJ', c: '#1FAE63' }] : []),
        ]
      : [{ v: String(totals.count), l: 'Steps', c: 'var(--fg-1)' }]),
    ...(ref ? [{ v: ref.value, l: ref.label, c: '#1FAE63' }] : []),
  ];
  return (
    <div style={s.totals}>
      {items.map((item, i) => (
        <div key={i} style={{ ...s.totStat, ...(i > 0 ? { borderLeft: '1px solid rgba(148,163,188,0.18)' } : {}) }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: item.c, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{item.v}</span>
          <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--fg-4)' }}>{item.l}</span>
        </div>
      ))}
    </div>
  );
}

const CopyIcon = ({ size = 11 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// ─── Step Card ─────────────────────────────────────────────────────────────────
interface StepCardProps {
  step: SimpleStep;
  idx: string | number;
  selected: boolean;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onDuplicate: (id: number) => void;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
  nested?: boolean;
}

function StepCard({ step, idx, selected, onSelect, onDelete, onDuplicate, workoutSettings, thresholds, nested }: StepCardProps) {
  const { t } = useTranslation();
  const z = ZONES[zoneFor((step.min + step.max) / 2)] || ZONES[1];
  const intensityLine = formatStepIntensityLine(
    step.min,
    step.max,
    workoutSettings.primaryIntensityMetric,
    thresholds,
  );
  return (
    <div
      onClick={() => onSelect(step.id)}
      style={{
        ...s.stepCard,
        boxShadow: selected ? 'var(--nm-inset-sm)' : nested ? 'var(--nm-flat)' : 'var(--nm-raised-sm)',
      }}
      onMouseEnter={e => { if (!selected) e.currentTarget.style.boxShadow = 'var(--nm-raised)'; }}
      onMouseLeave={e => { if (!selected) e.currentTarget.style.boxShadow = nested ? 'var(--nm-flat)' : 'var(--nm-raised-sm)'; }}>
      <div style={{ width: 4, flexShrink: 0, background: z.color }} />
      <div style={{ width: 28, flexShrink: 0, display: 'grid', placeItems: 'center', fontSize: 10.5, fontWeight: 800, color: 'var(--fg-4)', fontVariantNumeric: 'tabular-nums' }}>{idx}</div>
      <div style={{ flex: 1, padding: '9px 6px 9px 0', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-1)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{step.name === 'Active' ? t('workoutBuilder.palette.active') : step.name}</span>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 8.5, fontWeight: 800, padding: '2px 6px', borderRadius: 6, background: z.color + '22', color: z.color }}>{z.label}</span>
            {step.isRamp && <span style={{ fontSize: 8, fontWeight: 800, padding: '2px 5px', borderRadius: 4, background: 'var(--surface-sunken)', color: 'var(--fg-3)', letterSpacing: '0.04em' }}>RAMP</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600, color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />
            </svg>
            {fmtDur(step.duration)}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600, color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            {intensityLine}
          </span>
        </div>
      </div>
      <button
        type="button"
        title="Duplicate"
        style={s.stepActionBtn}
        onClick={e => { e.stopPropagation(); onDuplicate(step.id); }}
        onMouseEnter={e => (e.currentTarget.style.color = '#4A90D9')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--fg-4)')}>
        <CopyIcon />
      </button>
      <button
        type="button"
        title="Delete"
        style={s.stepActionBtn}
        onClick={e => { e.stopPropagation(); onDelete(step.id); }}
        onMouseEnter={e => (e.currentTarget.style.color = '#FF6B3D')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--fg-4)')}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ─── Repeat Block ──────────────────────────────────────────────────────────────
interface RepeatBlockProps {
  block: RepeatBlock;
  baseIdx: number;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onDuplicate: (id: number) => void;
  onUpdate: (patch: Partial<RepeatBlock>) => void;
  accent: string;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
}

function RepeatBlockComp({ block, baseIdx, selectedId, onSelect, onDelete, onDuplicate, onUpdate, accent, workoutSettings, thresholds }: RepeatBlockProps) {
  const [open, setOpen] = useState(true);
  const dur = block.steps.reduce((a, s) => a + s.duration, 0) * block.count;

  return (
    <div style={{ borderRadius: 16, background: 'var(--surface-0)', boxShadow: 'var(--nm-raised)', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', cursor: 'pointer', background: 'var(--surface-1)', transition: 'background 120ms' }}
        onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-sunken)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-1)')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.3" strokeLinecap="round">
            <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--fg-1)', whiteSpace: 'nowrap' }}>
            {block.name ?? 'Repeat'}
          </span>
          <div
            onClick={e => e.stopPropagation()}
            style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'var(--surface-0)', borderRadius: 8, boxShadow: 'var(--nm-inset-sm)', padding: '2px 3px', flexShrink: 0 }}>
            <button
              type="button"
              style={{ width: 22, height: 22, border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 15, fontWeight: 800, color: 'var(--fg-3)', borderRadius: 5, display: 'grid', placeItems: 'center' }}
              onClick={() => onUpdate({ count: Math.max(1, block.count - 1) })}>−</button>
            <span style={{ fontSize: 13, fontWeight: 800, padding: '0 4px', fontVariantNumeric: 'tabular-nums', color: accent }}>{block.count}×</span>
            <button
              type="button"
              style={{ width: 22, height: 22, border: 'none', cursor: 'pointer', background: 'transparent', fontSize: 15, fontWeight: 800, color: 'var(--fg-3)', borderRadius: 5, display: 'grid', placeItems: 'center' }}
              onClick={() => onUpdate({ count: block.count + 1 })}>+</button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-4)', fontVariantNumeric: 'tabular-nums' }}>{fmtDur(dur)}</span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            {open ? <path d="M18 15l-6-6-6 6" /> : <path d="M6 9l6 6 6-6" />}
          </svg>
          <button
            type="button"
            title="Duplicate"
            style={s.repeatActionBtn}
            onClick={e => { e.stopPropagation(); onDuplicate(block.id); }}
            onMouseEnter={e => (e.currentTarget.style.color = '#4A90D9')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--fg-4)')}>
            <CopyIcon />
          </button>
          <button
            type="button"
            title="Delete"
            style={s.repeatActionBtn}
            onClick={e => { e.stopPropagation(); onDelete(block.id); }}
            onMouseEnter={e => (e.currentTarget.style.color = '#FF6B3D')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--fg-4)')}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div style={{ padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {block.steps.map((sub, i) => (
            <StepCard key={sub.id} step={sub} idx={`${baseIdx}.${i + 1}`}
              selected={sub.id === selectedId}
              onSelect={onSelect}
              onDuplicate={onDuplicate}
              onDelete={() => onUpdate({ steps: block.steps.filter(x => x.id !== sub.id) })}
              workoutSettings={workoutSettings}
              thresholds={thresholds}
              nested />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Step List ─────────────────────────────────────────────────────────────────
interface StepListProps {
  steps: WorkoutStep[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onDelete: (id: number) => void;
  onDuplicate: (id: number) => void;
  onSetSteps: React.Dispatch<React.SetStateAction<WorkoutStep[]>>;
  accent: string;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
}

function StepList({ steps, selectedId, onSelect, onDelete, onDuplicate, onSetSteps, accent, workoutSettings, thresholds }: StepListProps) {
  let n = 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      {steps.map(step => {
        if (step.type === 'repeat') {
          const base = n + 1;
          n += step.steps.length;
          return (
            <RepeatBlockComp key={step.id} block={step} baseIdx={base}
              selectedId={selectedId} onSelect={onSelect} onDelete={onDelete} onDuplicate={onDuplicate}
              onUpdate={patch => onSetSteps(prev => prev.map(x => x.id === step.id ? { ...x, ...patch } as RepeatBlock : x) as WorkoutStep[])}
              accent={accent}
              workoutSettings={workoutSettings}
              thresholds={thresholds} />
          );
        }
        n++;
        return (
          <StepCard key={step.id} step={step} idx={n}
            selected={step.id === selectedId}
            onSelect={onSelect}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            workoutSettings={workoutSettings}
            thresholds={thresholds} />
        );
      })}
    </div>
  );
}

// ─── Step Editor (right panel) ─────────────────────────────────────────────────
interface StepEditorProps {
  step: SimpleStep;
  workoutSettings: WorkoutBuilderSettings;
  thresholds: IntensityThresholds;
  onUpdate: (patch: Partial<SimpleStep>) => void;
  onDelete: () => void;
  onClose: () => void;
  accent: string;
  variant?: 'panel' | 'sheet';
  editTitle?: string;
}

function StepEditor({ step, workoutSettings, thresholds, onUpdate, onDelete, onClose, accent, variant = 'panel', editTitle = 'Edit Step' }: StepEditorProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(step.name);
  const [dur, setDur] = useState(fmtDur(step.duration));
  const [mn, setMn] = useState(step.min);
  const [mx, setMx] = useState(step.max);
  const [ramp, setRamp] = useState(step.isRamp);
  const [applied, setApplied] = useState(false);
  const appliedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isSingle = workoutSettings.primaryIntensityTargetOrRange === 'single';
  const pctMax = intensityPercentMax(workoutSettings.primaryIntensityMetric);
  const metricLabel = intensityMetricShortLabel(workoutSettings.primaryIntensityMetric);
  const pctMid = Math.round((mn + mx) / 2);

  useEffect(() => {
    setName(step.name);
    setDur(fmtDur(step.duration));
    setMn(step.min);
    setMx(step.max);
    setRamp(step.isRamp);
  }, [step.id, step.name, step.duration, step.min, step.max, step.isRamp]);

  useEffect(() => () => {
    if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current);
  }, []);

  const zone = zoneFor(isSingle ? pctMid : (mn + mx) / 2);
  const z = ZONES[zone];
  const hintMin = isSingle ? pctMid : mn;
  const hintMax = isSingle ? pctMid : mx;
  const absHint = formatStepIntensityLine(
    hintMin,
    hintMax,
    workoutSettings.primaryIntensityMetric,
    thresholds,
    workoutSettings.primaryIntensityTargetOrRange,
  );

  /** Push local editor state into the parent step list. */
  const apply = () => {
    let finalMin = mn;
    let finalMax = mx;
    if (isSingle) {
      const pct = Math.min(Math.max(pctMid, 0), pctMax);
      finalMin = finalMax = pct;
    }
    onUpdate({
      name: name.trim() || step.name,
      duration: parseDur(dur),
      min: finalMin,
      max: finalMax,
      isRamp: ramp,
      zone: zoneFor((finalMin + finalMax) / 2),
    });
    setApplied(true);
    if (appliedTimerRef.current) clearTimeout(appliedTimerRef.current);
    appliedTimerRef.current = setTimeout(() => setApplied(false), 1500);
  };

  return (
    <div
      className="title-bar-no-drag"
      style={{
        width: variant === 'sheet' ? '100%' : 316,
        height: variant === 'sheet' ? 'auto' : '100%',
        maxHeight: variant === 'sheet' ? 'calc(88vh - 48px)' : undefined,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      {/* Header */}
      {variant === 'panel' && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px 12px', flexShrink: 0, borderBottom: '1px solid rgba(148,163,188,0.18)' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--fg-1)' }}>{editTitle}</span>
        <button type="button" style={s.seCloseBtn} onClick={onClose}
          onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)')}
          onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--nm-flat)')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      )}

      {/* Zone badge */}
      <div style={{ margin: '12px 16px 0', borderRadius: 10, padding: '8px 12px', border: `1px solid ${z.color}44`, background: z.color + '18', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: z.color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 700, flex: 1, color: z.color }}>{z.label} — {z.name}</span>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>{absHint}</span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Name */}
        <div style={s.seFieldLbl}>Step Name</div>
        <div style={s.seInputWrap}>
          <input style={{ ...s.seInput, userSelect: 'text' }} value={name}
            onChange={e => setName(e.target.value)}
            onBlur={apply} />
        </div>

        {/* Duration or distance */}
        <div style={s.seFieldLbl}>
          {workoutSettings.primaryLengthMetric === 'distance' ? 'Distance' : 'Duration'}
        </div>
        <div style={s.seInputWrap}>
          <input
            style={{ ...s.seInput, userSelect: 'text' }}
            value={workoutSettings.primaryLengthMetric === 'distance' ? String(step.duration) : dur}
            onChange={e => {
              if (workoutSettings.primaryLengthMetric === 'distance') {
                const meters = Math.max(0, parseInt(e.target.value, 10) || 0);
                onUpdate({ duration: meters });
              } else {
                setDur(e.target.value);
              }
            }}
            onBlur={workoutSettings.primaryLengthMetric === 'distance' ? undefined : apply}
          />
          <span style={s.seUnit}>
            {workoutSettings.primaryLengthMetric === 'distance' ? 'm' : 'mm:ss'}
          </span>
        </div>

        {/* Step type */}
        <div style={s.seFieldLbl}>Step Type</div>
        <div style={{ display: 'flex', background: 'var(--surface-sunken)', borderRadius: 10, boxShadow: 'var(--nm-inset-sm)', padding: 3, gap: 2 }}>
          {([['Steady', false], ['Ramp', true]] as [string, boolean][]).map(([label, val]) => (
            <button key={label} type="button"
              style={{ flex: 1, height: 32, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, borderRadius: 7, transition: 'all 150ms',
                background: ramp === val ? 'var(--surface-0)' : 'transparent',
                boxShadow: ramp === val ? 'var(--nm-raised-sm)' : 'none',
                color: ramp === val ? accent : 'var(--fg-4)',
              }}
              onClick={() => { setRamp(val); onUpdate({ isRamp: val }); }}>{label}</button>
          ))}
        </div>

        {/* Intensity target */}
        <div style={s.seFieldLbl}>
          {isSingle ? t('workoutBuilder.intensityAbsoluteTitle') : `Range — ${metricLabel}`}
        </div>
        {isSingle ? (
          <>
            <div style={s.seInputWrap}>
              <input
                style={{ ...s.seInput, userSelect: 'text' }}
                type="number"
                min={0}
                max={pctMax}
                value={pctMid}
                onChange={e => {
                  const v = Math.min(Math.max(parseInt(e.target.value, 10) || 0, 0), pctMax);
                  setMn(v);
                  setMx(v);
                }}
                onBlur={apply}
              />
              <span style={s.seUnit}>%</span>
            </div>
            <input
              type="range"
              min={0}
              max={pctMax}
              value={pctMid}
              style={s.seSlider}
              onChange={e => {
                const v = +e.target.value;
                setMn(v);
                setMx(v);
              }}
              onMouseUp={apply}
            />
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--fg-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Min</div>
                <div style={s.seInputWrap}>
                  <input style={{ ...s.seInput, userSelect: 'text' }} type="number" min={0} max={pctMax - 1} value={mn}
                    onChange={e => setMn(Math.min(parseInt(e.target.value) || 0, mx - 1))}
                    onBlur={apply} />
                  <span style={s.seUnit}>%</span>
                </div>
              </div>
              <span style={{ paddingBottom: 6, fontSize: 14, color: 'var(--fg-4)', flexShrink: 0 }}>→</span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--fg-4)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Max</div>
                <div style={s.seInputWrap}>
                  <input style={{ ...s.seInput, userSelect: 'text' }} type="number" min={1} max={pctMax} value={mx}
                    onChange={e => setMx(Math.max(parseInt(e.target.value) || 1, mn + 1))}
                    onBlur={apply} />
                  <span style={s.seUnit}>%</span>
                </div>
              </div>
            </div>
            <input type="range" min={0} max={pctMax} value={mn}
              style={s.seSlider}
              onChange={e => setMn(Math.min(+e.target.value, mx - 1))}
              onMouseUp={apply} />
            <input type="range" min={0} max={pctMax} value={mx}
              style={s.seSlider}
              onChange={e => setMx(Math.max(+e.target.value, mn + 1))}
              onMouseUp={apply} />
          </>
        )}

        {/* Zone visualizer */}
        <div style={{ height: 8, borderRadius: 4, display: 'flex', overflow: 'hidden', boxShadow: 'var(--nm-inset-sm)' }}>
          {Object.entries(ZONES).map(([k, zz]) => (
            <div key={k} style={{
              flex: 1, height: '100%', background: zz.color,
              opacity: parseInt(k) === zone ? 1 : 0.2,
              borderRadius: k === '1' ? '4px 0 0 4px' : k === '5' ? '0 4px 4px 0' : 0,
            }} />
          ))}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, marginTop: -2, fontVariantNumeric: 'tabular-nums', color: z.color }}>
          {z.label} — {z.name} · {absHint}
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 8, padding: '12px 16px 16px', borderTop: '1px solid rgba(148,163,188,0.18)', flexShrink: 0 }}>
        <button type="button" style={s.seDelBtn} onClick={onDelete}
          onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)')}
          onMouseLeave={e => (e.currentTarget.style.boxShadow = 'var(--nm-flat)')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
          </svg>
          Delete
        </button>
        <button
          type="button"
          style={{ flex: 1, height: 36, borderRadius: 10, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, color: '#fff', background: applied ? '#1FAE63' : `linear-gradient(135deg,${accent}cc,${accent})`, transition: 'background 180ms, opacity 120ms' }}
          onMouseEnter={e => { if (!applied) e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={e => { if (!applied) e.currentTarget.style.opacity = '1'; }}
          onClick={apply}>{applied ? 'Applied!' : 'Apply Changes'}</button>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export interface WorkoutBuilderEditConfig {
  /** Calendar workout — PATCH /workout/{id} */
  workoutId?: number;
  /** Library template — PUT /library/exercise/{id} */
  libraryExerciseId?: number;
  workoutTypeId?: number;
  /** Calendar day YYYY-MM-DD (calendar workouts only). */
  initialDate?: string;
  initialTitle: string;
  /** Native Garmin blocks — preferred for mobile layout. */
  initialDragData?: ApiStructBlock[];
  initialSteps: BuilderWorkoutStep[];
  structureMeta?: {
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
  };
  onSaved: () => void;
  onCancel: () => void;
}

export interface WorkoutBuilderInitialDraft { title: string; structure: ApiWorkoutStructure; workoutTypeId?: number; date: string }
interface WorkoutBuilderPageProps {
  isCoach?: boolean;
  initialDraft?: WorkoutBuilderInitialDraft;
  edit?: WorkoutBuilderEditConfig;
  /** Required for creating new workouts from the sidebar builder. */
  userId?: number;
  /** Called after a new workout is created successfully. */
  onCreated?: () => void;
  /** Mobile vertical layout (Ai-Tempo) vs desktop 3-pane. PWA defaults to mobile. */
  layout?: 'desktop' | 'mobile';
}

/** Pick run sport type for running structured workouts. */
function pickRunningTypeId(types: { id: number; english_name?: string; name?: string }[]): number | null {
  if (!types.length) return null;
  const match = types.find(t => {
    const label = `${t.english_name ?? ''} ${t.name ?? ''}`.toLowerCase();
    return label.includes('run') && !label.includes('brick');
  });
  return match?.id ?? types[0].id;
}

/** Pick cycling/ride sport type when creating a structured workout. */
function pickCyclingTypeId(types: { id: number; english_name?: string; name?: string }[]): number | null {
  if (!types.length) return null;
  const match = types.find(t => {
    const label = `${t.english_name ?? ''} ${t.name ?? ''}`.toLowerCase();
    return label.includes('ride') || label.includes('cycl') || label.includes('bike');
  });
  return match?.id ?? types[0].id;
}

function pickWorkoutTypeId(
  settings: WorkoutBuilderSettings,
  types: { id: number; english_name?: string; name?: string }[],
): number | null {
  if (!types.length) return null;
  return isRunningBuilderSettings(settings)
    ? pickRunningTypeId(types)
    : pickCyclingTypeId(types);
}

export function WorkoutBuilderPage({ edit, initialDraft, userId, onCreated, layout = 'mobile' }: WorkoutBuilderPageProps = {}) {
  const { t } = useTranslation();
  const isMobileLayout = layout === 'mobile';
  const accent = 'var(--color-accent)';
  const accentHex = '#DF2063';
  const {
    ftp: athleteFtp,
    maxHr: athleteMaxHr,
    ltHr: athleteLtHr,
    thresholdSpeedMps: athleteThresholdSpeed,
  } = useAthleteMetrics(userId ?? null);
  const FTP = athleteFtp != null && athleteFtp > 0 ? athleteFtp : 265;
  const thresholds: IntensityThresholds = useMemo(() => ({
    ftp: FTP,
    maxHr: athleteMaxHr != null && athleteMaxHr > 0 ? athleteMaxHr : 0,
    ltHr: athleteLtHr != null && athleteLtHr > 0 ? athleteLtHr : 0,
    thresholdSpeedMps:
      athleteThresholdSpeed != null && athleteThresholdSpeed > 0 ? athleteThresholdSpeed : 0,
  }), [FTP, athleteMaxHr, athleteLtHr, athleteThresholdSpeed]);

  const initialSettings = parseWorkoutBuilderSettings(
    (edit?.structureMeta ?? initialDraft?.structure) as Partial<WorkoutBuilderSettings> | undefined,
  );
  const [workoutSettings, setWorkoutSettings] = useState<WorkoutBuilderSettings>(initialSettings);
  const [draftSettings, setDraftSettings] = useState<WorkoutBuilderSettings>(initialSettings);
  const [settingsConfirmed, setSettingsConfirmed] = useState(!!(edit?.structureMeta?.primaryIntensityMetric ?? initialDraft?.structure.primaryIntensityMetric));
  const [setupModalOpen, setSetupModalOpen] = useState(!settingsConfirmed);

  const [workoutName, setWorkoutName] = useState(edit?.initialTitle ?? initialDraft?.title ?? '');
  const [steps, setSteps] = useState<WorkoutStep[]>(() => {
    const initial = edit?.initialSteps as WorkoutStep[] | undefined;
    if (initial?.length) return initial;
    if (initialDraft) return apiStructureToBuilderSteps(initialDraft.structure, { ftp: FTP, ltHr: athleteLtHr ?? undefined, thresholdSpeedMps: athleteThresholdSpeed ?? undefined, primaryIntensityMetric: initialSettings.primaryIntensityMetric }) as WorkoutStep[];
    return isMobileLayout ? [] : INIT_STEPS;
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [workoutTypeId, setWorkoutTypeId] = useState<number | null>(initialDraft?.workoutTypeId ?? null);
  const [workoutTypes, setWorkoutTypes] = useState<{ id: number; english_name?: string; name?: string }[]>([]);

  const totals = useMemo(
    () => calcTotals(steps, workoutSettings, thresholds),
    [steps, workoutSettings, thresholds],
  );
  const thresholdRef = referenceThresholdLabel(
    workoutSettings.primaryIntensityMetric,
    thresholds,
  );
  const selStep = findStep(steps, selectedId);
  const isEditMode = !!edit;
  const chartTitle = chartTitleForMetric(workoutSettings.primaryIntensityMetric);
  const mobilePaletteTemplates = useMemo(
    () => BLOCK_TEMPLATES.filter(t => (MOBILE_PALETTE_KEYS as readonly string[]).includes(t.key)),
    [],
  );
  const paletteLabel = useCallback(
    (tpl: BlockTemplate) => t(`workoutBuilder.palette.${tpl.key}`, tpl.name),
    [t],
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedId(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /** Load default sport type for new structured workouts. */
  useEffect(() => {
    if (isEditMode) return;
    workouts.getWorkoutTypes()
      .then(res => {
        const types = res.data.data?.workoutTypes ?? [];
        setWorkoutTypes(types);
        setWorkoutTypeId(initialDraft?.workoutTypeId ?? pickWorkoutTypeId(workoutSettings, types));
      })
      .catch(() => {
        setWorkoutTypes([]);
        setWorkoutTypeId(null);
      });
  }, [isEditMode]);

  const openSettingsModal = useCallback(() => {
    setDraftSettings(workoutSettings);
    setSetupModalOpen(true);
  }, [workoutSettings]);

  const confirmSettings = useCallback(() => {
    setWorkoutSettings(draftSettings);
    setSettingsConfirmed(true);
    setSetupModalOpen(false);
    if (!isEditMode && workoutTypes.length) {
      setWorkoutTypeId(pickWorkoutTypeId(draftSettings, workoutTypes));
    }
  }, [draftSettings, isEditMode, workoutTypes]);

  /** Persist structured workout — update existing or create new. */
  const handleSave = useCallback(async () => {
    if (!settingsConfirmed) {
      setDraftSettings(workoutSettings);
      setSetupModalOpen(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const structure = builderStepsToApiStructure(steps as BuilderWorkoutStep[], {
    ...workoutSettings,
    thresholdSpeedMps: thresholds.thresholdSpeedMps,
    ftp: thresholds.ftp,
    ltHr: thresholds.ltHr,
    maxHr: thresholds.maxHr,
  });
      const payload: Record<string, unknown> = {
        title: workoutName.trim() || 'Untitled Workout',
        structure,
        total_time_planned: totals.secs > 0 ? totals.secs : null,
        tss_planned: totals.tss > 0 ? totals.tss : null,
        if_planned: Number(totals.ifVal) > 0 ? Number(totals.ifVal) : null,
      };
      if (totals.meters > 0) {
        payload.distance_planned = totals.meters;
      }

      if (edit) {
        if (edit.libraryExerciseId) {
          const exercisePayload: Record<string, unknown> = { ...payload };
          if (edit.workoutTypeId) exercisePayload.workout_type_id = edit.workoutTypeId;
          await library.updateExercise(edit.libraryExerciseId, { data: { exercise: exercisePayload } });
        } else if (edit.workoutId) {
          await workouts.updateWorkout(edit.workoutId, payload);
        } else {
          setSaveError('Missing workout or library exercise id.');
          return;
        }
        edit.onSaved();
        return;
      }

      if (!userId) {
        setSaveError('Select an athlete before saving this workout.');
        return;
      }
      if (!workoutTypeId) {
        setSaveError('Workout type is still loading. Please try again.');
        return;
      }

      const today = new Date().toISOString().slice(0, 10);
      await forms.createWorkout(userId, {
        ...payload,
        workout_type_id: workoutTypeId,
        workout_day: `${initialDraft?.date ?? today} 00:00:00`,
      });
      onCreated?.();
    } catch (err: unknown) {
      const apiMsg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSaveError(apiMsg ?? 'Failed to save workout. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [edit, initialDraft, steps, workoutName, totals, userId, workoutTypeId, onCreated, settingsConfirmed, workoutSettings]);

  const updateStep = useCallback((id: number, patch: Partial<SimpleStep>) => {
    const upd = (ss: WorkoutStep[]): WorkoutStep[] =>
      ss.map(step => {
        if (step.id === id && step.type === 'simple') return { ...step, ...patch };
        if (step.type === 'repeat') return { ...step, steps: step.steps.map(x => x.id === id ? { ...x, ...patch } : x) };
        return step;
      });
    setSteps(prev => upd(prev));
  }, []);

  const deleteStep = useCallback((id: number) => {
    setSteps(prev =>
      prev
        .filter(s => s.id !== id)
        .map(s => s.type === 'repeat' ? { ...s, steps: s.steps.filter(x => x.id !== id) } : s)
    );
    setSelectedId(cur => cur === id ? null : cur);
  }, []);

  /** Insert a copy immediately after the matching top-level or nested step/block. */
  const duplicateStep = useCallback((id: number) => {
    const newId = Date.now();
    let selectAfter = newId;

    setSteps(prev => {
      const repeatIdx = prev.findIndex(s => s.type === 'repeat' && s.id === id);
      if (repeatIdx >= 0) {
        const copy = cloneRepeatBlock(prev[repeatIdx] as RepeatBlock, newId);
        const next = [...prev];
        next.splice(repeatIdx + 1, 0, copy);
        selectAfter = copy.steps[0]?.id ?? newId;
        return next;
      }

      const simpleIdx = prev.findIndex(s => s.type === 'simple' && s.id === id);
      if (simpleIdx >= 0) {
        const copy = cloneSimpleStep(prev[simpleIdx] as SimpleStep, newId);
        const next = [...prev];
        next.splice(simpleIdx + 1, 0, copy);
        return next;
      }

      return prev.map(s => {
        if (s.type !== 'repeat') return s;
        const subIdx = s.steps.findIndex(x => x.id === id);
        if (subIdx < 0) return s;
        const copy = cloneSimpleStep(s.steps[subIdx], newId);
        const newSteps = [...s.steps];
        newSteps.splice(subIdx + 1, 0, copy);
        return { ...s, steps: newSteps };
      });
    });

    setSelectedId(selectAfter);
  }, []);

  const addBlock = (t: BlockTemplate) => {
    const baseId = Date.now();
    if (t.steps?.length) {
      setSteps(prev => [...prev, buildNamedRepeatBlock(baseId, t.name, t.steps!)]);
      setSelectedId(null);
      return;
    }
    const newStep: SimpleStep = {
      id: baseId,
      type: 'simple',
      name: t.name,
      duration: t.duration,
      min: t.min,
      max: t.max,
      zone: t.zone,
      isRamp: t.isRamp,
    };
    setSteps(prev => [...prev, newStep]);
    setSelectedId(baseId);
  };

  const addRepeat = () => {
    const id = Date.now();
    setSteps(prev => [...prev, { ...buildNamedRepeatBlock(id, 'Interval', INTERVAL_PAIR_STEPS), count: 3 }]);
  };

  if (isMobileLayout) {
    return (
      <MobileWorkoutBuilderShell
        initialDraft={initialDraft}
        edit={edit}
        userId={userId}
        onCreated={onCreated}
        ftpValue={FTP}
        ltHrValue={thresholds.ltHr}
        maxHrValue={thresholds.maxHr}
        thresholdSpeedMps={thresholds.thresholdSpeedMps}
      />
    );
  }

  // ─── Desktop 3-pane layout (legacy) ─────────────────────────────────────────
  const handleSetupCancel = () => {
    if (settingsConfirmed) {
      setDraftSettings(workoutSettings);
      setSetupModalOpen(false);
      return;
    }
    edit?.onCancel?.();
  };

  const setupModal = (
    <WorkoutBuilderSetupModal
      open={setupModalOpen}
      settings={draftSettings}
      onChange={setDraftSettings}
      onConfirm={confirmSettings}
      onCancel={settingsConfirmed || edit?.onCancel ? handleSetupCancel : undefined}
      confirmed={settingsConfirmed}
    />
  );

  return (
    <div style={isEditMode ? { ...s.page, margin: 0, height: '100vh' } : s.page}>

      {/* ── Top bar ── */}
      <div className="title-bar-no-drag" style={s.topBar}>
        <div style={s.tbDiv} />
        <div style={{ flex: 1, minWidth: 0, maxWidth: 320 }}>
          <input style={{ ...s.tbNameInput, userSelect: 'text' }}
            value={workoutName}
            onChange={e => setWorkoutName(e.target.value)}
            placeholder="Workout name…" />
        </div>
        <div style={s.tbSport}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
            <path d="M15 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zm-3 11.5V12l-3-3 3-1.5" />
            <path d="M12 12l3 1.5" />
          </svg>
          {isRunningBuilderSettings(workoutSettings) ? 'Running' : 'Cycling'}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {[
            { v: fmtDur(totals.secs), l: 'DURATION', c: 'var(--fg-1)' },
            ...(totals.meters > 0
              ? [{ v: `${(totals.meters / 1000).toFixed(2)} km`, l: 'DISTANCE', c: 'var(--fg-1)' as const }]
              : []),
            ...(totals.tss > 0
              ? [
                  { v: String(totals.tss), l: 'TSS', c: accentHex },
                  { v: totals.ifVal, l: 'IF', c: 'var(--fg-1)' as const },
                ]
              : []),
            ...(workoutSettings.primaryIntensityMetric === 'percentOfFtp'
              ? [{ v: `${FTP} W`, l: 'FTP', c: '#1FAE63' as const }]
              : []),
            ...(thresholdRef
              ? [{ v: thresholdRef.value, l: thresholdRef.label.toUpperCase(), c: '#1FAE63' as const }]
              : []),
          ].map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ width: 1, height: 16, background: 'rgba(148,163,188,0.18)', margin: '0 8px' }} />}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, padding: '0 6px' }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: stat.c, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{stat.v}</span>
                <span style={{ fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--fg-4)' }}>{stat.l}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
          {saveError && (
            <span style={{ fontSize: 11, fontWeight: 600, color: '#EF4444', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {saveError}
            </span>
          )}
          {isEditMode && (
            <button type="button" style={s.btnGhost} onClick={() => edit?.onCancel()} disabled={saving}>
              Cancel
            </button>
          )}
          <button
            type="button"
            style={{ ...s.btnPrimary, background: `linear-gradient(135deg,${accentHex}cc,${accentHex})`, boxShadow: `0 4px 16px ${accentHex}44`, opacity: saving ? 0.7 : 1 }}
            disabled={saving}
            onClick={() => void handleSave()}>
            {saving ? 'Saving…' : 'Save & Close'}
          </button>
        </div>
      </div>

      {/* ── Intensity chart ── */}
      <div style={s.chartSec}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--fg-4)' }}>
            {chartTitle}
          </div>
          {settingsConfirmed && (
            <button
              type="button"
              onClick={openSettingsModal}
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 10,
                fontWeight: 700,
                color: accentHex,
                background: 'var(--surface-sunken)',
                boxShadow: 'var(--nm-flat)',
              }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
              {settingsSummaryLabel(workoutSettings)}
            </button>
          )}
        </div>
        <PowerChart
          steps={steps}
          selectedId={selectedId}
          onSelect={setSelectedId}
          workoutSettings={workoutSettings}
          thresholds={thresholds}
        />
      </div>

      {/* ── 3-pane main ── */}
      <div style={s.mainArea}>

        {/* Left: block library */}
        <BlockPalette onAddBlock={addBlock} onAddRepeat={addRepeat} accent={accentHex} />

        {/* Center: step list */}
        <div style={s.centerPanel}>
          <TotalsStrip totals={totals} accent={accentHex} workoutSettings={workoutSettings} thresholds={thresholds} />
          <StepList
            steps={steps} selectedId={selectedId}
            onSelect={setSelectedId} onDelete={deleteStep} onDuplicate={duplicateStep}
            onSetSteps={setSteps} accent={accentHex}
            workoutSettings={workoutSettings}
            thresholds={thresholds}
          />
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button style={s.addBtn}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)'; e.currentTarget.style.color = 'var(--fg-2)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--nm-flat)'; e.currentTarget.style.color = accentHex; }}
              onClick={() => addBlock(BLOCK_TEMPLATES[0])}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add Step
            </button>
            <button style={{ ...s.addBtn, color: 'var(--fg-4)' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--nm-raised-sm)'; e.currentTarget.style.color = 'var(--fg-2)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--nm-flat)'; e.currentTarget.style.color = 'var(--fg-4)'; }}
              onClick={addRepeat}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
              Add Repeat
            </button>
          </div>
        </div>

        {/* Right: step editor (slide-in) — fixed height so footer buttons stay visible */}
        <div style={{
          width: selStep ? 316 : 0,
          flexShrink: 0,
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--surface-0)',
          borderLeft: '1px solid rgba(148,163,188,0.18)',
          transition: 'width 280ms cubic-bezier(0.22,1,0.36,1)',
        }}>
          {selStep && (
            <StepEditor
              step={selStep}
              workoutSettings={workoutSettings}
              thresholds={thresholds}
              onUpdate={p => updateStep(selStep.id, p)}
              onDelete={() => deleteStep(selStep.id)}
              onClose={() => setSelectedId(null)}
              accent={accentHex} />
          )}
        </div>

      </div>

      {setupModal}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    background: 'var(--surface-0)',
    /* Extend top/right/bottom only — negative left margin clips under contentInner overflow:hidden */
    margin: '-20px 0 -32px 0',
  },
  topBar: {
    flexShrink: 0,
    height: 60,
    background: 'var(--surface-0)',
    boxShadow: '0 2px 16px rgba(163,177,198,0.22)',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '0 20px',
    zIndex: 10,
  },
  tbDiv: {
    width: 1,
    height: 20,
    background: 'rgba(148,163,188,0.18)',
    flexShrink: 0,
  },
  tbNameInput: {
    width: '100%',
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontFamily: 'var(--font-display)',
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--fg-1)',
    letterSpacing: '-0.02em',
  },
  tbSport: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 10px',
    borderRadius: 8,
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-flat)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--fg-3)',
    flexShrink: 0,
  },
  btnGhost: {
    height: 34,
    padding: '0 16px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--fg-3)',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-sm)',
    transition: 'box-shadow 140ms ease, color 140ms ease',
  },
  btnPrimary: {
    height: 34,
    padding: '0 18px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontSize: 12,
    fontWeight: 800,
    color: '#fff',
    transition: 'opacity 120ms ease',
  },
  chartSec: {
    flexShrink: 0,
    height: 168,
    background: 'var(--surface-0)',
    borderBottom: '1px solid rgba(148,163,188,0.18)',
    padding: '10px 20px 0',
    boxShadow: '0 4px 18px rgba(163,177,198,0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  mainArea: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    minHeight: 0,
  },
  leftPanel: {
    width: 320,
    flexShrink: 0,
    minHeight: 0,
    alignSelf: 'stretch',
    background: 'var(--surface-0)',
    borderInlineEnd: '1px solid rgba(148,163,188,0.18)',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '16px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    boxSizing: 'border-box',
  },
  sectLbl: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--fg-4)',
  },
  blockGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 8,
    width: '100%',
  },
  blockPill: {
    border: 'none',
    cursor: 'pointer',
    borderRadius: 14,
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-sm)',
    padding: '10px 8px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    transition: 'box-shadow 140ms ease, transform 100ms ease',
    fontFamily: 'var(--font-display)',
    textAlign: 'start',
    boxSizing: 'border-box',
  },
  repeatAddBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    justifyContent: 'center',
    height: 36,
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-flat)',
    fontFamily: 'var(--font-display)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--fg-3)',
    transition: 'box-shadow 140ms ease, color 140ms ease',
  },
  centerPanel: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    minWidth: 0,
  },
  totals: {
    borderRadius: 14,
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-raised-sm)',
    padding: '10px 16px',
    display: 'flex',
    flexShrink: 0,
  },
  totStat: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
  },
  stepCard: {
    borderRadius: 13,
    background: 'var(--surface-0)',
    display: 'flex',
    alignItems: 'stretch',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'box-shadow 170ms ease',
  },
  stepActionBtn: {
    width: 34,
    flexShrink: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--fg-4)',
    transition: 'color 120ms ease',
  },
  repeatActionBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--fg-4)',
    width: 24,
    height: 24,
    borderRadius: 6,
    transition: 'color 120ms ease',
  },
  addBtn: {
    flex: 1,
    height: 40,
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-flat)',
    fontFamily: 'var(--font-display)',
    fontSize: 11,
    fontWeight: 700,
    color: '#DF2063',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    transition: 'box-shadow 140ms ease, color 140ms ease',
  },
  seCloseBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-flat)',
    display: 'grid',
    placeItems: 'center',
    color: 'var(--fg-3)',
    transition: 'box-shadow 120ms ease',
  },
  seFieldLbl: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: 'var(--fg-4)',
  },
  seInputWrap: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--surface-sunken)',
    borderRadius: 10,
    boxShadow: 'var(--nm-inset-sm)',
    overflow: 'hidden',
  },
  seInput: {
    flex: 1,
    height: 36,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    padding: '0 12px',
    fontFamily: 'var(--font-display)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--fg-1)',
    minWidth: 0,
  },
  seUnit: {
    padding: '0 10px 0 2px',
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--fg-4)',
  },
  seSlider: {
    WebkitAppearance: 'none',
    appearance: 'none',
    width: '100%',
    height: 4,
    borderRadius: 2,
    outline: 'none',
    cursor: 'pointer',
    background: 'var(--surface-sunken)',
    boxShadow: 'var(--nm-inset-sm)',
  },
  seDelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: 36,
    padding: '0 14px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'var(--font-display)',
    fontSize: 11,
    fontWeight: 700,
    color: '#FF6B3D',
    background: 'var(--surface-0)',
    boxShadow: 'var(--nm-flat)',
    transition: 'box-shadow 140ms ease',
  },
};
